import { useState, useEffect, useMemo } from 'react';
import firebase from 'firebase/app';

export function useTeamMembersTips(teamId, membersList, dateRange) {
    const [memberTipsData, setMemberTipsData] = useState({});
    const [memberPayoutsData, setMemberPayoutsData] = useState({});
    const [memberRefundedData, setMemberRefundedData] = useState({});
    const [totalPoolAmount, setTotalPoolAmount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Calculate tips for each member in the selected date range
    useEffect(() => {
        if (!dateRange || !teamId || !membersList || membersList.length === 0) {
            setMemberTipsData({});
            setMemberPayoutsData({});
            setMemberRefundedData({});
            setTotalPoolAmount(0);
            setLoading(false);
            setError(null);
            return;
        }

        const fetchMemberTips = async () => {
            setLoading(true);
            setError(null);
            const tipsData = {};
            const payoutsData = {};
            let totalPool = 0;

            try {
                const db = firebase.firestore();
                
                // Fetch tips and payouts for each member
                const refundedData = {};
                for (const member of membersList) {
                    let memberTipsTotal = 0;
                    let memberPayoutsTotal = 0;
                    let memberRefundedTotal = 0;

                    // 1. Fetch tips received by member (net amount after fees)
                    // Use 'created' to filter by when transaction actually occurred
                    const memberTipsQuery = db
                        .collectionGroup('taps')
                        .where('teamId', '==', teamId)
                        .where('clientId', '==', member.clientId)
                        .where('status', 'in', ['pending', 'available_requested', 'available'])
                        .where('created', '>=', dateRange.startDateUTC)
                        .where('created', '<=', dateRange.endDateUTC);

                    const memberTipsSnapshot = await memberTipsQuery.get();
                    
                    memberTipsSnapshot.forEach(doc => {
                        const tap = doc.data();
                        // Use netAmount if available, otherwise calculate: amount - fee
                        const netAmount = tap.netAmount || (tap.amount - (tap.fee || 0));
                        memberTipsTotal += netAmount || 0;
                    });

                    // 2. Fetch refunded taps - use updatedAt to filter by when refund occurred
                    // Refunded taps should be deducted from the total
                    // Note: product filter required for Firestore index compatibility
                    const refundedTapsQuery = db
                        .collectionGroup('taps')
                        .where('teamId', '==', teamId)
                        .where('clientId', '==', member.clientId)
                        .where('product', '==', 'tipping')
                        .where('status', '==', 'refunded')
                        .where('updatedAt', '>=', dateRange.startDateUTC)
                        .where('updatedAt', '<=', dateRange.endDateUTC);

                    const refundedTapsSnapshot = await refundedTapsQuery.get();
                    
                    refundedTapsSnapshot.forEach(doc => {
                        const tap = doc.data();
                        // Use netAmount if available, otherwise calculate: amount - fee
                        const netAmount = tap.netAmount || (tap.amount - (tap.fee || 0));
                        // Track refunded amount separately
                        memberRefundedTotal += netAmount || 0;
                        // Deduct refunded amount from total
                        memberTipsTotal -= netAmount || 0;
                    });

                    refundedData[member.clientId] = memberRefundedTotal;

                    // 3. Fetch payouts made to member (team-to-member transfers)
                    const memberPayoutsQuery = db
                        .collection('transfers')
                        .where('fromTeamId', '==', teamId)
                        .where('toClientId', '==', member.clientId)
                        .where('transferType', '==', 'team-to-member')
                        .where('status', '==', 'sent')
                        .where('createdAt', '>=', dateRange.startDateUTC)
                        .where('createdAt', '<=', dateRange.endDateUTC);

                    const memberPayoutsSnapshot = await memberPayoutsQuery.get();
                    
                    memberPayoutsSnapshot.forEach(doc => {
                        const transfer = doc.data();
                        console.log(`Payout for ${member.clientId}:`, transfer);
                        memberPayoutsTotal += transfer.payoutAmountCents || 0;
                    });

                    tipsData[member.clientId] = memberTipsTotal;
                    payoutsData[member.clientId] = memberPayoutsTotal;
                    totalPool += memberTipsTotal + memberPayoutsTotal;
                }

                // Debug logging removed

                setMemberTipsData(tipsData);
                setMemberPayoutsData(payoutsData);
                setMemberRefundedData(refundedData);
                setTotalPoolAmount(totalPool);
            } catch (error) {
                console.error('Error fetching member tips:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchMemberTips();
    }, [dateRange, teamId, membersList]);

    // Return array of members with their tips data
    const membersWithTips = useMemo(() => {
        if (!membersList || membersList.length === 0) {
            return [];
        }
        
        return membersList.map(member => {
            const tips = memberTipsData[member.clientId] || 0;
            const payouts = memberPayoutsData[member.clientId] || 0;
            const refunded = memberRefundedData[member.clientId] || 0;
            const total = tips + payouts;
            
            return {
                ...member,
                tips,
                payouts,
                refunded,
                total,
            };
        });
    }, [membersList, memberTipsData, memberPayoutsData, memberRefundedData]);

    // Calculate total refunded amount
    const totalRefunded = useMemo(() => {
        return Object.values(memberRefundedData).reduce((sum, amount) => sum + amount, 0);
    }, [memberRefundedData]);

    return [
        membersWithTips,
        totalPoolAmount,
        totalRefunded,
        loading,
        error,
    ];
}
